import React from "react";

import { twJoin } from "tailwind-merge";

import {
  CellDisplayType,
  isExplicitActivationCell,
} from "src/cases/caseView/blocks/cellTypes";
import { TypedCellDisplay } from "src/cases/caseView/blocks/TypedCellDisplay";
import { TypedCellEditor } from "src/cases/caseView/blocks/TypedCellEditor";
import {
  useIsCellEditing,
  useIsCellSelected,
  useSelectionActions,
} from "src/cases/caseView/blocks/useEditableTableSelection";

type EditableTableCellProps = {
  value: unknown;
  columnKey: string;
  rowIndex: number;
  isEditable: boolean;
  isEdited: boolean;
  displayType: CellDisplayType;
  columnValues?: unknown[];
  onChange: (newValue: unknown) => void;
};

export const EditableTableCell: React.FC<EditableTableCellProps> = ({
  value,
  columnKey,
  rowIndex,
  isEditable,
  isEdited,
  displayType,
  columnValues,
  onChange,
}) => {
  const isSelected = useIsCellSelected(rowIndex, columnKey);
  const isEditing = useIsCellEditing(rowIndex, columnKey);
  const { selectCell, deselectCell, exitEditing, setEditing } =
    useSelectionActions();

  // Selection happens on mousedown so we can call preventDefault()
  // (prevents browser focus changes) and blur the old editor to commit
  // its pending value. Explicit-activation editors (boolean, date, enum,
  // tags, list, object) are NOT opened here — see handleClick below.
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isEditable) return;
    if (!isSelected) {
      e.preventDefault();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      selectCell(rowIndex, columnKey);
    } else if (isExplicitActivationCell(displayType.type) && !isEditing) {
      e.preventDefault();
    }
  };

  // Editor activation for explicit-activation cells happens on click.
  // This avoids a race with useOnClickOutside (which fires on mousedown):
  // by the time click fires, the old editor's mousedown click-outside has
  // already completed and cleaned up.
  const handleClick = () => {
    if (!isEditable || !isSelected) return;
    if (isExplicitActivationCell(displayType.type) && !isEditing) {
      setEditing();
    }
  };

  // Bind exitEditing to this cell's coordinates so that a stale
  // click-outside handler from a previous editor is a no-op once
  // the selection has moved to a different cell.
  const handleBlur = isExplicitActivationCell(displayType.type)
    ? () => exitEditing(rowIndex, columnKey)
    : deselectCell;

  const showEditor =
    isEditable &&
    (isExplicitActivationCell(displayType.type) ? isEditing : isSelected);

  return (
    <div
      className={twJoin(
        "flex h-8 w-full items-center",
        "font-inter-normal-12px text-gray-800",
        isEditable && "cursor-text",
        !isEditable && "cursor-default bg-gray-50 text-gray-500",
        isSelected &&
          isEditable &&
          "bg-indigo-50 outline outline-1 outline-indigo-500",
        !isSelected && isEdited && "bg-yellow-50",
      )}
      data-loc={`editable-table-cell-${rowIndex}-${columnKey}`}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
    >
      {showEditor ? (
        <TypedCellEditor
          columnKey={columnKey}
          columnValues={columnValues}
          displayType={displayType}
          value={value}
          onBlur={handleBlur}
          onChange={onChange}
        />
      ) : (
        <div className="flex min-w-0 flex-1 py-1.5 pr-2 pl-1.5">
          <TypedCellDisplay
            columnKey={columnKey}
            displayType={displayType}
            value={value}
            disableHover
          />
        </div>
      )}
    </div>
  );
};
